import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH1_QUESTIONS } from "../data/neetPhysicsCh1Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 1: Units and Measurements (NCERT Class 11, Ch. 1-2).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Physical Quantities & Units", "Dimensional Analysis", "Errors in Measurement", "Measuring Instruments"];

const SECTION_COLORS = {
  "Physical Quantities & Units": "#2563eb",
  "Dimensional Analysis":        "#7c3aed",
  "Errors in Measurement":       "#dc2626",
  "Measuring Instruments":       "#16a34a",
};

const CONCEPTS = [
  // ── Physical Quantities & Units ────────────────────────────────────────────
  { section: "Physical Quantities & Units", title: "Fundamental (base) quantities and SI base units",
    explanation: "In the SI system there are seven base (fundamental) quantities, each with one base unit: length (metre, m), mass (kilogram, kg), time (second, s), electric current (ampere, A), thermodynamic temperature (kelvin, K), amount of substance (mole, mol), and luminous intensity (candela, cd). Every other physical quantity's unit is DERIVED by combining these base units — there are no independent units beyond these seven.",
    example: `Base quantities (SI):
Length      -> metre (m)
Mass        -> kilogram (kg)
Time        -> second (s)
Current     -> ampere (A)
Temperature -> kelvin (K)
Amount of substance -> mole (mol)
Luminous intensity  -> candela (cd)` },
  { section: "Physical Quantities & Units", title: "Derived units expressed in base units",
    explanation: "A derived unit is built from base units via the quantity's defining equation. For example, speed = distance/time gives units of m/s; force = mass x acceleration gives kg.m/s2, named the newton (N); energy = force x distance gives kg.m2/s2, named the joule (J).",
    example: `Force (N)     = kg . m . s^-2      (F = ma)
Energy (J)    = kg . m^2 . s^-2    (W = F . d)
Power (W)     = kg . m^2 . s^-3    (P = W / t)
Pressure (Pa) = kg . m^-1 . s^-2   (P = F / A)` },
  { section: "Physical Quantities & Units", title: "SI prefixes and order of magnitude",
    explanation: "SI prefixes scale a unit by powers of 10 without changing the underlying unit -- e.g. kilo- (10^3), milli- (10^-3), micro- (10^-6), nano- (10^-9), mega- (10^6), giga- (10^9). 'Order of magnitude' rounds a number to the nearest power of 10: write N = a x 10^b with 1 <= a < 10; if a < 5 the order of magnitude is 10^b, if a >= 5 it rounds up to 10^(b+1).",
    example: `5 x 10^-3 m  = 5 millimetres (mm)
2.5 x 10^6 Hz = 2.5 megahertz (MHz)

3,200 = 3.2 x 10^3  -> order of magnitude 10^3 (3.2 < 5)
7,200 = 7.2 x 10^3  -> order of magnitude 10^4 (7.2 >= 5, rounds up)` },
  { section: "Physical Quantities & Units", title: "Practical units and unit systems (CGS, FPS, SI)",
    explanation: "Besides SI (metre-kilogram-second), older systems include CGS (centimetre-gram-second) and FPS (foot-pound-second). Converting a quantity's numeric value between systems uses n1.u1 = n2.u2, since the numeric value n and the unit size u are inversely related for the same physical quantity -- a bigger unit needs a smaller number to represent the same magnitude.",
    example: `n1.u1 = n2.u2   (value x unit-size stays constant)

Convert 1 newton to dyne (CGS force unit):
1 N = 1 kg.m/s^2 = (1000 g)(100 cm)/s^2 = 10^5 g.cm/s^2 = 10^5 dyne` },
  { section: "Physical Quantities & Units", title: "Dimensionless quantities and pure numbers",
    explanation: "Some quantities -- like angle (radian), strain, refractive index, and specific gravity -- are ratios of two LIKE quantities, so their units cancel out and they carry no dimensions or units at all, even though they were still obtained from measurement.",
    example: `Strain = dL / L            (length / length -> dimensionless)
Angle (rad) = arc / radius  (length / length -> dimensionless)
Refractive index = c / v    (speed / speed -> dimensionless)` },

  // ── Dimensional Analysis ────────────────────────────────────────────────────
  { section: "Dimensional Analysis", title: "Dimensions and dimensional formula",
    explanation: "The dimension of a physical quantity shows which base quantities (and to what powers) combine to produce it, written using [M] mass, [L] length, [T] time, [A] current, [K] temperature, [mol] amount, [cd] luminous intensity. The dimensional FORMULA expresses a derived quantity as [M^a L^b T^c ...].",
    example: `Velocity     = displacement / time  -> [M0 L1 T-1]
Acceleration = velocity / time      -> [M0 L1 T-2]
Force = mass x acceleration         -> [M1 L1 T-2]
Work  = force x displacement        -> [M1 L2 T-2]` },
  { section: "Dimensional Analysis", title: "Principle of homogeneity of dimensions",
    explanation: "A physically correct equation must have the SAME dimensions on both sides, and every term being added or subtracted in that equation must independently share that same dimension too -- you can never validly add a length to a time, for instance.",
    example: `s = ut + (1/2)at^2
[L] = [L T-1][T] + [L T-2][T2]
[L] = [L] + [L]     <- dimensionally consistent` },
  { section: "Dimensional Analysis", title: "Applications: checking the correctness of equations",
    explanation: "Dimensional analysis can PROVE an equation is wrong (dimensions mismatch) but can NEVER prove an equation is fully correct, since it is blind to dimensionless numerical constants (like the '2' in v^2 = u^2 + 2as, or '1/2' in KE = (1/2)mv^2).",
    example: `Check: v^2 = u^2 + 2as
[L T-1]^2 =? [L T-1]^2 + [L T-2][L]
[L2 T-2]  =  [L2 T-2] + [L2 T-2]   <- consistent
(this method still can't confirm the constant "2" is correct)` },
  { section: "Dimensional Analysis", title: "Applications: deriving relations between quantities",
    explanation: "If a quantity is believed to depend on a product of powers of other quantities, dimensional analysis can find those powers by matching dimensions on both sides -- this is how the formula for a simple pendulum's time period is derived up to an unknown dimensionless constant.",
    example: `Assume T = k . l^a . g^b   (k = dimensionless constant)
[T] = [L]^a [L T-2]^b = [L^(a+b) T^(-2b)]
Matching powers: a+b = 0, -2b = 1  ->  b = -1/2, a = 1/2
So T = k . sqrt(l/g)   -- matches the real formula (k = 2*pi)` },
  { section: "Dimensional Analysis", title: "Limitations of dimensional analysis",
    explanation: "Dimensional analysis cannot determine dimensionless constants (like 2*pi), cannot distinguish between terms of the same dimension combined differently (e.g. a+b vs a-b), and cannot handle relations involving trigonometric, exponential, or logarithmic functions, since these functions' arguments must themselves be dimensionless.",
    example: `sin(theta), e^x, log(x)  -> the ARGUMENT must be dimensionless
Dimensional analysis alone cannot derive formulas containing these` },

  // ── Errors in Measurement ───────────────────────────────────────────────────
  { section: "Errors in Measurement", title: "Systematic errors vs random errors",
    explanation: "Systematic errors occur in one predictable direction because of a flaw in the method or instrument (e.g. instrumental zero error) and can often be corrected once identified. Random errors arise from unpredictable, uncontrollable fluctuations (e.g. observer's judgment, minor environmental changes) and are reduced -- but never eliminated -- by taking many readings and averaging.",
    example: `Systematic: a screw gauge with zero error always reads 0.02 mm
            too high -> SUBTRACT 0.02 mm from every reading
Random: repeated timing of a pendulum's period varies slightly
            each trial due to human reaction time -> take the MEAN` },
  { section: "Errors in Measurement", title: "Absolute, mean absolute, relative and percentage error",
    explanation: "If a is the mean of n measured values a1...an, the absolute error in the i-th reading is |a - ai|. The mean absolute error is the average of these absolute errors. Relative error is the mean absolute error divided by the mean value, and percentage error is the relative error x 100%.",
    example: `Readings: 2.63, 2.56, 2.42, 2.71, 2.80 s -> mean a = 2.624 s
Deviations: 0.006, 0.064, 0.204, 0.086, 0.176 (absolute values)
Mean absolute error ~ 0.11 s
Relative error = 0.11 / 2.62 ~ 0.04  ->  percentage error ~ 4%` },
  { section: "Errors in Measurement", title: "Combination of errors — sum, difference, product, quotient",
    explanation: "When two measured quantities are added or subtracted, their ABSOLUTE errors add (even for a difference -- errors never cancel). When quantities are multiplied or divided, their RELATIVE (fractional) errors add. When a quantity is raised to a power, its relative error is multiplied by that power.",
    example: `Z = A + B  or  Z = A - B  ->  dZ = dA + dB          (absolute add)
Z = A x B  or  Z = A / B  ->  dZ/Z = dA/A + dB/B     (relative add)
Z = A^n                    ->  dZ/Z = n . (dA/A)      (scaled by power)` },
  { section: "Errors in Measurement", title: "Significant figures — counting rules",
    explanation: "Significant figures are the digits in a measurement known reliably plus one estimated digit. Rules: all non-zero digits are significant; zeros BETWEEN non-zero digits are significant; leading zeros are NEVER significant; trailing zeros after a decimal point ARE significant; trailing zeros in a whole number without a decimal point are ambiguous unless written in scientific notation.",
    example: `0.0025   -> 2 significant figures (25) -- leading zeros don't count
2.30     -> 3 significant figures -- trailing zero after decimal counts
1500     -> ambiguous (2, 3, or 4 sig figs?)
1.500 x 10^3 -> unambiguously 4 significant figures` },
  { section: "Errors in Measurement", title: "Significant figures in arithmetic operations",
    explanation: "In ADDITION/SUBTRACTION, the result is rounded to the SAME NUMBER OF DECIMAL PLACES as the term with the FEWEST decimal places. In MULTIPLICATION/DIVISION, the result is rounded to the SAME NUMBER OF SIGNIFICANT FIGURES as the factor with the FEWEST significant figures.",
    example: `12.11 + 18.0 + 1.012 = 31.122  ->  rounds to 31.1
  (18.0 has only 1 decimal place, so the answer is limited to 1)

4.237 x 2.1 = 8.8977  ->  rounds to 8.9
  (2.1 has only 2 significant figures, so the answer is limited to 2)` },

  // ── Measuring Instruments ───────────────────────────────────────────────────
  { section: "Measuring Instruments", title: "Vernier calipers — principle and least count",
    explanation: "A vernier caliper has a main scale and a sliding vernier scale with divisions slightly smaller than the main scale's. Its least count = (1 main scale division) - (1 vernier scale division), typically 0.01 cm for a common lab caliper (10 vernier divisions matching 9 main scale divisions of 1 mm each).",
    example: `1 MSD = 1 mm, 10 VSD = 9 MSD -> 1 VSD = 0.9 mm
Least count = 1 MSD - 1 VSD = 1 - 0.9 = 0.1 mm = 0.01 cm` },
  { section: "Measuring Instruments", title: "Zero error in vernier calipers and screw gauge",
    explanation: "Zero error occurs when the instrument's zero doesn't align with true zero even when the jaws/anvil are fully closed. A POSITIVE zero error (reading is to the right of true zero) must be SUBTRACTED from every observed reading; a NEGATIVE zero error must be ADDED.",
    example: `Corrected reading = Observed reading - Zero error (with its sign)

If zero error = +0.05 cm and observed reading = 2.40 cm:
Corrected reading = 2.40 - 0.05 = 2.35 cm` },
  { section: "Measuring Instruments", title: "Screw gauge (micrometer) — pitch and least count",
    explanation: "A screw gauge's PITCH is the linear distance the spindle advances in ONE complete rotation of the circular (thimble) scale. Its least count = pitch / (number of divisions on the circular scale) -- typically giving a least count of 0.01 mm, finer than a vernier caliper's.",
    example: `Pitch = 1 mm, circular scale has 100 divisions
Least count = 1 mm / 100 = 0.01 mm

Reading = (main scale reading) + (circular scale reading x least count)` },
  { section: "Measuring Instruments", title: "Parallax method for measuring large distances",
    explanation: "Parallax is the apparent shift in an object's position when viewed from two different points. For very large/astronomical distances, two observation points separated by a known baseline measure the parallax ANGLE to a distant object, and the distance is found using D = baseline / angle (in radians), since the angle is very small.",
    example: `D = b / theta    (theta in radians, b = baseline between the
                    two observation points)

Basis of triangulation -- used to estimate distances to nearby
stars (using Earth's orbit as the baseline) and planets` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh1Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh1Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh1Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH1_QUESTIONS : NEET_PHYSICS_CH1_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 1</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Units and Measurements</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 11, Ch. 1-2 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH1_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH1_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
